import type { FC, ReactNode } from 'react'
import type { Tone } from '@/types'

import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'
import { cn } from '@/lib/utils'
import { useIsDesktop } from '@/hooks/use-media-query'
import { Reveal } from '@/components/motion/reveal'
import { CountUp } from '@/components/motion/roll-text'

import {
    DynamicKycVisual,
    SoftApprovalVisual,
    StingBackVisual
} from '@/components/landing/shared/product-visuals'

type Panel = {
    id: string
    tone: Tone
    index: string
    name: string
    headline: string
    body: ReactNode
    visual: ReactNode
}

const Registered: FC = (): ReactNode => (
    <sup className='text-[0.45em] align-super'>
        ®
    </sup>
)

const PANELS: Panel[] = [
    {
        id: 'softapproval',
        tone: 'ink',
        index: 'A',
        name: 'SoftApproval',
        headline: 'Extract more from your declines.',
        body: (
            <>
                <p>
                    SoftApproval<Registered /> leverages behavioral economics to distinguish fraud-suspected buyers from actual fraudsters.
                </p>
                <p>
                    Customers whose transactions have been flagged for SoftApproval<Registered /> are presented with a challenge unique to their buyer profile and products are released to those who pass.
                </p>
            </>
        ),
        visual: <SoftApprovalVisual className='w-full max-w-xl' />
    },
    {
        id: 'stingback',
        tone: 'void',
        index: 'B',
        name: 'StingBack',
        headline: 'Massive waste of time. Worth every penny.',
        body: (
            <>
                <p>
                    nSure.ai’s patented StingBack<Registered /> protocol gives fraudsters every indication their transaction was successful, even going so far as to deliver a zero-value key or download code.
                </p>
                <p>
                    The transaction is refunded to ensure the cardholder isn’t billed for undelivered goods, but not before wasting the fraudsters time and delivering a lethal blow to their reputation on the black market upon trying to resell the goods.
                </p>
                <p>
                    StingBack<Registered /> packs a punch in systemically dismantling the scalability of payment fraud and serves as a proactive deterrent for those who might otherwise try to defraud your company in the future.
                </p>
            </>
        ),
        visual: <StingBackVisual />
    },
    {
        id: 'dynamickyc',
        tone: 'paper',
        index: 'C',
        name: 'DynamicKYC',
        headline: 'KYC without TMI.',
        body: (
            <>
                <p>
                    Know Your Customer is an AML tool. As an anti-fraud measure, it leads to decline rates of up to 20%. Our data shows that only 2% of those transactions are actual payment fraud.
                </p>
                <dl className='grid gap-6 pt-2 sm:grid-cols-2'>
                    <div className='flex flex-col gap-2 border-t border-tone-line pt-4'>
                        <dt className='t-label text-tone'>
                            Impact on acquisition
                        </dt>
                        <dd>
                            <span className='mb-2 block font-heading text-5xl font-semibold tracking-tight text-tone wdth-75'>
                                <CountUp to={65} suffix='%' />
                            </span>
                            Up to 65% of the false positives our clients decline are attributed to new users. Imagine what you lose if it costs seven times more to acquire a new customer than to retain an old one.
                        </dd>
                    </div>
                    <div className='flex flex-col gap-2 border-t border-tone-line pt-4'>
                        <dt className='t-label text-tone'>
                            Impact on retention
                        </dt>
                        <dd>
                            <span className='mb-2 block font-heading text-5xl font-semibold tracking-tight text-tone wdth-75'>
                                <CountUp to={95} suffix='%' />
                            </span>
                            Research shows a 5% increase in retention improves profitability by over 25% and can lead to a 95% increase in profits.
                        </dd>
                    </div>
                </dl>
            </>
        ),
        visual: <DynamicKycVisual />
    }
]

const PanelContent: FC<{ panel: Panel }> = ({ panel }): ReactNode => (
    <div className='grid h-full gap-12 lg:grid-cols-12 lg:items-center lg:gap-10'>
        <div className='flex flex-col gap-8 lg:col-span-6 xl:col-span-5'>
            <div className='flex items-center gap-4 t-label text-tone-muted'>
                <span className='flex size-8 items-center justify-center border border-tone-line text-tone'>
                    {panel.index}
                </span>
                Proprietary technology
            </div>

            <h3 className='font-heading text-[clamp(3rem,6.4vw,6.5rem)] font-semibold uppercase leading-[0.86] tracking-[-0.04em] wdth-75'>
                {panel.name}
                <sup className='ml-1 align-super text-[0.3em] text-tone-accent'>
                    ®
                </sup>
            </h3>

            <p className='font-serif text-[clamp(1.6rem,2.4vw,2.4rem)] italic leading-[1.1] text-tone-accent'>
                {panel.headline}
            </p>

            <div className='flex max-w-xl flex-col gap-4 text-[0.98rem] leading-relaxed text-tone-muted'>
                {panel.body}
            </div>
        </div>

        <div className='flex justify-center lg:col-span-6 xl:col-span-6 xl:col-start-7'>
            {panel.visual}
        </div>
    </div>
)

// Three chapters slide past horizontally while the section is pinned on large
// screens; on small screens they simply stack.
export const Arsenal: FC = (): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const isDesktop = useIsDesktop()
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] })
    const x = useTransform(scrollYProgress, [0.04, 0.96], ['0vw', `-${(PANELS.length - 1) * 100}vw`])
    const progress = useTransform(scrollYProgress, [0, 1], [0, 1])

    return (
        <section id='arsenal' aria-labelledby='arsenal-title' className='relative'>
            <div className='px-5 pb-6 pt-28 md:px-8 lg:px-12 lg:pt-40'>
                <Reveal className='flex flex-col gap-6 md:flex-row md:items-end md:justify-between'>
                    <h2 id='arsenal-title' className='max-w-4xl font-heading t-h1 font-semibold uppercase wdth-75'>
                        Three ways to stop fraud
                        <span className='block font-serif font-normal normal-case italic tracking-normal text-tone-accent wdth-100'>
                            without stopping customers.
                        </span>
                    </h2>
                    <p className='max-w-xs text-sm leading-relaxed text-tone-muted'>
                        Proprietary technology built for digital goods: recover good customers, sting the fraudsters, verify only what is required.
                    </p>
                </Reveal>
            </div>

            <div ref={ref} className='relative lg:h-[340vh]'>
                {PANELS.map((panel, position) => (
                    <div
                        key={`marker-${panel.id}`}
                        aria-hidden
                        data-section-tone={panel.tone}
                        className='pointer-events-none absolute inset-x-0 hidden h-[80vh] lg:block'
                        style={{ top: `${50 + position * 80}vh` }}
                    />
                ))}

                <div className='lg:sticky lg:top-0 lg:h-svh lg:overflow-hidden'>
                    <motion.div
                        className='flex flex-col lg:h-full lg:w-max lg:flex-row'
                        style={{ x: isDesktop ? x : '0vw' }}
                    >
                        {PANELS.map((panel, position) => (
                            <article
                                key={panel.id}
                                id={panel.id}
                                className={cn(
                                    'relative px-5 py-20 md:px-8 lg:h-full lg:w-screen lg:px-12 lg:py-0 lg:pt-24',
                                    position > 0 && 'border-t border-tone-line lg:border-l lg:border-t-0'
                                )}
                            >
                                <div aria-hidden data-section-tone={panel.tone} className='pointer-events-none absolute inset-0 lg:hidden' />
                                <PanelContent panel={panel} />
                            </article>
                        ))}
                    </motion.div>

                    <div className='absolute inset-x-12 bottom-8 hidden h-px bg-tone-line lg:block' aria-hidden>
                        <motion.span className='block h-full origin-left bg-tone' style={{ scaleX: progress }} />
                    </div>
                </div>
            </div>
        </section>
    )
}
